import { SerializedStyles } from "@emotion/react";
import { RefObject, memo, useCallback, useRef, useState } from "react";
import { Checkbox, FormFlowPage, LocationInput, TextInput } from "src/components";
import { noInput } from "src/utilities/input";

import { types } from "@fraction/shared";

import { usePartialSubmit } from "../hooks";
import { styles } from "../styles";

export interface ApplicantAddressProps {
  loanAddress?: types.ExtractedPropertyAddress;
  value?: types.ExtractedPropertyAddress;
  onChange?: (location?: types.ExtractedPropertyAddress) => void;
  title?: string;
  onNext: () => void;
  style?: SerializedStyles;
  focusRef?: RefObject<HTMLInputElement>;
}

const ApplicantAddress = ({
  loanAddress,
  value,
  onChange,
  onNext,
  style,
  focusRef,
  title,
  ...props
}: ApplicantAddressProps) => {
  const validate = useCallback(() => !noInput(value?.thoroughfare), [value]);
  const { handleNext, nextDisabled, nextLoading } = usePartialSubmit({ onNext, validate });

  const inputRef = useRef<HTMLInputElement>(null);
  const mergedRef = focusRef || inputRef;

  const [sameAsPropertyAddress, setSameAsPropertyAddress] = useState<boolean | undefined>(undefined);

  const handleChangeSameAsPropertyAddress = useCallback(() => {
    const next = !sameAsPropertyAddress;
    setSameAsPropertyAddress((prev) => !prev);
    onChange?.(next ? loanAddress : undefined);
    if (next) {
      setTimeout(() => {
        // focus and blur so that it shows the beginning of the input
        mergedRef?.current?.focus();
        mergedRef?.current?.blur();
      }, 100);
    } else {
      mergedRef?.current?.focus();
    }
  }, [sameAsPropertyAddress, setSameAsPropertyAddress, onChange, mergedRef, loanAddress]);

  const handleChangePremise = useCallback(
    (premise?: string) => {
      onChange?.({ ...value, premise });
    },
    [onChange, value]
  );

  const handleChangeLocation = useCallback(
    (location?: types.ExtractedPropertyAddress) => {
      onChange?.({ ...location, premise: value?.premise });
    },
    [onChange, value]
  );

  return (
    <FormFlowPage
      {...props}
      title={title || "Where do you live?"}
      description="Fraction works with primary residences, secondary residences, and investment properties."
      onNext={handleNext}
      style={style}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
    >
      {loanAddress?.thoroughfare && (
        <Checkbox
          labelStyle={styles.checkboxLabel}
          style={styles.checkbox}
          checked={sameAsPropertyAddress}
          onChange={handleChangeSameAsPropertyAddress}
          label="Same address as subject property"
        />
      )}
      <LocationInput
        disabled={sameAsPropertyAddress}
        ref={mergedRef}
        name="applicantAddress"
        label="Property address"
        placeholder="1234 Electronic Ave, Vancouver, British Columbia, CA"
        style={styles.topInput}
        onSelect={handleChangeLocation}
        value={value}
        locationTypes={["address"]}
      />
      <TextInput
        disabled={sameAsPropertyAddress}
        style={styles.input}
        label="Unit number (optional)"
        placeholder="5"
        value={value?.premise || ""}
        onChange={handleChangePremise}
      />
    </FormFlowPage>
  );
};

export default memo(ApplicantAddress);
